/**
 * A `clock` SSE message: the current server time.
 */
export interface ClockMessage {
	/** Timestamp of the current server time, as an ISO-8601-like string (see {@link parseClockTime}). */
	time: string;
}

/**
 * Parses the `time` field of a {@link ClockMessage}.
 *
 * The backend serializes timestamps with up to 7 fractional-second digits (.NET tick
 * precision), e.g. `2026-08-29T12:04:51.5714846+03:30`. The ECMAScript Date Time String
 * Format only supports exactly 3 fractional digits, and some engines (notably
 * Safari/WebKit) return an Invalid Date for anything else. Truncating to millisecond
 * precision keeps the offset/timezone intact while parsing reliably everywhere.
 */
export function parseClockTime(value: string): Date {
	return new Date(value.replace(/(\.\d{3})\d+/, '$1'));
}

export type MarketClockState = 'closed' | 'preTrading' | 'trading';

export interface TradingPeriod {
	readonly type: 'preTrading' | 'trading';
	readonly start: string;
	readonly end: string;
}

export interface ScheduledTradingPeriod extends TradingPeriod {
	readonly startSeconds: number;
	readonly endSeconds: number;
}

export interface MarketDaySchedule {
	readonly state: MarketClockState;
	readonly periods: readonly TradingPeriod[];
}

export interface TradingDayPeriods {
	readonly dayLabel: string;
	readonly isToday: boolean;
	readonly periods: readonly TradingPeriod[];
}

export interface MarketCountdown {
	readonly target: Date;
	readonly next: MarketClockState;
}

export interface MarketTimeSlicerSegment {
	readonly type: MarketClockState;
	readonly startMinutes: number;
	readonly left: string;
	readonly width: string;
}

export interface MarketTimeLabel {
	readonly time: string;
	readonly left: string;
}

export interface MarketTimeLabels {
	readonly upper: readonly MarketTimeLabel[];
	readonly lower: readonly MarketTimeLabel[];
}
